// Deterministic daily verse + question generation.
// The same date always yields the same verse AND the same questions/distractors
// for every player — that's what makes the "daily drop" a shared ritual and lets
// score cards be comparable. Seeded from the date string, no server needed.

import type { DailyVerse, Question } from '@/types'
import { VERSE_POOL, BIBLE_BOOKS, type VerseSeed } from './pool'
import { triviaBooks, triviaRoundFor } from './trivia'
import { DEFAULT_TRANSLATION } from '@/lib/config'

// --- seeded RNG (mulberry32) ------------------------------------------------
export function mulberry32(seed: number) {
  return function () {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function hashString(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function shuffle<T>(arr: T[], rng: () => number): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function uniq(arr: string[]): string[] {
  return Array.from(new Set(arr))
}

// Common words we never want to blank out for a fill-in-the-blank question —
// they'd make a weak, guessable prompt.
export const STOP_WORDS = new Set([
  'which', 'where', 'these', 'there', 'their', 'would', 'could', 'should',
  'because', 'before', 'after', 'about', 'through', 'against', 'everyone',
  'whatever', 'themselves', 'yourselves', 'another', 'things', 'people',
  'shall', 'those', 'while', 'every', 'himself', 'therefore',
])

// Pick a second, distinctive content word from the verse text (>= 5 letters,
// not the primary keyword, not a filler word) so a verse can offer a fresh
// fill-in-the-blank on replay.
function pickBlankWord(text: string, avoid: string, rng: () => number): string | null {
  const words = (text.match(/\b[A-Za-z]{5,}\b/g) || []).filter(
    (w) => w.toLowerCase() !== avoid.toLowerCase() && !STOP_WORDS.has(w.toLowerCase()),
  )
  if (!words.length) return null
  return words[Math.floor(rng() * words.length)]
}

// Build one multiple-choice question with plausible distractors.
function buildMC(
  prompt: string,
  correct: string,
  candidatePool: string[],
  rng: () => number,
  teach: string,
  n = 4,
): Question | null {
  const distractors = shuffle(
    uniq(candidatePool).filter((c) => c && c !== correct),
    rng,
  ).slice(0, n - 1)
  if (distractors.length < 1) return null
  const options = shuffle([correct, ...distractors], rng)
  return { prompt, options, answerIndex: options.indexOf(correct), teach }
}

export function generateQuestions(seed: VerseSeed, rng: () => number): Question[] {
  const others = VERSE_POOL.filter((v) => v.reference !== seed.reference)
  const books = others.map((v) => v.book)
  const speakers = others.map((v) => v.speaker)
  const audiences = others.map((v) => v.audience)
  const events = others.flatMap((v) => [v.before, v.after])
  const themes = others.map((v) => v.theme)
  const keywords = others.map((v) => v.keyword)
  const references = others.map((v) => v.reference)

  const candidates: (Question | null)[] = []
  // What each question can teach BEYOND its own answer — set beside it here,
  // attached in `teachMore` once the five are chosen. Kept off the Question
  // so nothing here touches the rng or the deal.
  const more = new Map<Question, string[]>()
  const aud = midSentence(seed.audience)
  const spk = midSentence(seed.speaker)
  const add = (q: Question | null, extra: string[]) => { if (q) more.set(q, extra) }

  {
    const q = buildMC(
      'Which book of the Bible is this verse from?',
      seed.book,
      books,
      rng,
      `This verse is ${seed.reference}, from the book of ${seed.book}.`,
    )
    add(q, [`Just before it: ${seed.before}`])
    candidates.push(q)
  }

  {
    const q = buildMC(
      'Who is speaking in this verse?',
      seed.speaker,
      speakers,
      rng,
      `Here the words come from ${seed.speaker}.`,
    )
    add(q, [`Spoken to ${aud}.`, `Just before: ${seed.before}`])
    candidates.push(q)
  }

  {
    const q = buildMC(
      'Who is being addressed?',
      seed.audience,
      audiences,
      rng,
      `These words are directed to ${seed.audience}.`,
    )
    add(q, [`Spoken by ${spk}.`, `Just before: ${seed.before}`])
    candidates.push(q)
  }

  {
    const q = buildMC(
      'What happens just before this verse?',
      seed.before,
      events,
      rng,
      `Just before: ${seed.before}`,
    )
    add(q, [`Right after: ${seed.after}`])
    candidates.push(q)
  }

  {
    const q = buildMC(
      'What comes right after this verse?',
      seed.after,
      events,
      rng,
      `Right after: ${seed.after}`,
    )
    add(q, [`Just before it: ${seed.before}`])
    candidates.push(q)
  }

  // Fill-in-the-blank from the verse text itself.
  const re = new RegExp(`\\b${seed.keyword}\\b`, 'i')
  if (re.test(seed.text)) {
    const blanked = seed.text.replace(re, '_____')
    const q = buildMC(
      `Fill the blank: "${blanked}"`,
      seed.keyword,
      keywords,
      rng,
      `The missing word is "${seed.keyword}."`,
    )
    // Tagged AFTER buildMC, so marking it consumes no rng and every deal is
    // byte-identical to the one before the tag existed. See localize.ts.
    if (q) { const b = { ...q, blank: true }; add(b, [`Its theme: ${seed.theme}.`]); candidates.push(b) }
  }

  {
    const q = buildMC(
      'What is the main theme of this verse?',
      seed.theme,
      themes,
      rng,
      `The heart of this verse is: ${seed.theme}.`,
    )
    add(q, [`Spoken by ${spk} to ${aud}.`])
    candidates.push(q)
  }

  // Reference recall — which citation is this verse?
  {
    const q = buildMC(
      'What is the reference for this verse?',
      seed.reference,
      references,
      rng,
      `This verse is ${seed.reference}.`,
    )
    add(q, [`Right after it: ${seed.after}`])
    candidates.push(q)
  }

  // A second fill-in-the-blank on a different word, so the same verse can feel
  // fresh across replays.
  const blank2 = pickBlankWord(seed.text, seed.keyword, rng)
  if (blank2) {
    const re2 = new RegExp(`\\b${blank2}\\b`, 'g')
    const blanked2 = seed.text.replace(re2, '_____')
    const q = buildMC(
      `Fill the blank: "${blanked2}"`,
      blank2,
      [...keywords, ...others.map((v) => v.keyword)],
      rng,
      `The missing word is "${blank2}."`,
    )
    if (q) { const b = { ...q, blank: true }; add(b, [`Its theme: ${seed.theme}.`]); candidates.push(b) }
  }

  // Keep a deterministic set of valid questions.
  const verseQuestions = shuffle(candidates.filter((q): q is Question => q !== null), rng)

  // FIVE QUESTIONS ABOUT THIS VERSE. All five.
  //
  // The last slot briefly held a BONUS TRIVIA question about the whole book the
  // verse came from, and it has been taken back out: the daily drop is the one
  // place in this app that teaches a specific verse, and spending its most
  // valuable slot on a fact about the book was spending it on something else.
  // Trivia did not go away — it has two rounds of its own now (`/play/trivia`
  // and `/study/trivia`) plus a whole battle mode, which is a better home for
  // it than one seat inside a run about something else.
  //
  // **Taking it out RESTORES the historical deal rather than re-dealing it, and
  // that is not luck — the original wiring was built for this.** The bonus was
  // appended AFTER `verseQuestions` was already shuffled, so it consumed rng
  // nobody else was waiting on. Dropping the call leaves questions one through
  // four untouched and puts back, in slot five, exactly the question this
  // generator produced before trivia existed. Verified by running both
  // derivations over the pool rather than reasoned about.
  //
  // If a bonus question is ever wanted back, `bonusTriviaFor` is still in
  // `./trivia.ts` and still the only thing that builds one — and it must go on
  // the END for the same reason, or every run in the app's history re-deals.
  // If a line's own extras would all spoil a later question, it may borrow any
  // other true thing about the verse; the same guards still apply.
  const fallback = [
    ...seed.facts,
    `Spoken by ${spk} to ${aud}.`,
    `Its theme: ${seed.theme}.`,
    `Just before: ${seed.before}`,
    `Right after: ${seed.after}`,
    `It's in the ${seed.testament === 'OT' ? 'Old' : 'New'} Testament.`,
  ]
  return teachMore(verseQuestions.slice(0, 5), more, seed.facts, fallback)
}

// A wrong answer has to TEACH, and "The missing word is 'Resist.'" only told
// somebody what they had just been shown (found by playing it, 2026-10-29: the
// result screen's "3 things you now know" listed three answers back). So each
// line keeps its answer FIRST — `MissedList` leads with a line that names it —
// and then says one more true thing out of the verse's own data: who it was
// said to, what happened around it, or one of the pool's hand-written `facts`,
// which had been shown on two screens and never here. One extra per line and
// none repeated in a run, so five misses read as five different things.
//
// The guard that matters: a line is read the moment its question is answered,
// so it must never contain the answer to a question still to come ("Right
// after: …" on Q1 when Q3 asks what comes right after). Any extra that names a
// later question's right option is dropped, per extra.
//
// It reads nothing random and changes no prompt, option or order — the deal is
// byte-identical to the one before, checked over 400 dates.
function teachMore(qs: Question[], more: Map<Question, string[]>, facts: string[], fallback: string[]): Question[] {
  const answers = qs.map((q) => q.options[q.answerIndex].toLowerCase())
  const used = new Set<string>()
  return qs.map((q, i) => {
    const later = answers.slice(i + 1)
    // Not a later answer, not already added, and not what another line in the
    // run already says ("Right after: …" is both the after-question's answer
    // and the reference question's extra).
    const core = (e: string) => e.replace(/^(Just before|Right after)( it)?: /, '')
    const safe = (e: string) =>
      !used.has(core(e)) &&
      !later.some((a) => a.length > 2 && e.toLowerCase().includes(a)) &&
      !qs.some((o) => o !== q && o.teach.includes(core(e)))
    // ONE more thing, never two, and never the same thing twice in a run: the
    // fact first (it is the one written to teach), else the line's own context.
    const pick = [...(facts[i] ? [facts[i]] : []), ...(more.get(q) ?? []), ...fallback].find(safe)
    if (!pick) return q
    used.add(core(pick))
    return { ...q, teach: `${q.teach} ${pick}` }
  })
}

// "The Israelites" reads wrong after "to"; "Jesus" and "Paul" must not change.
// Only an article or possessive at the front is lowered — a name never is.
const LOWER_FIRST = new Set(['the', 'his', 'her', 'their', 'a', 'an', 'all', 'readers', 'anyone', 'everyone'])
function midSentence(s: string): string {
  const [first, ...rest] = s.split(' ')
  return LOWER_FIRST.has(first.toLowerCase()) ? [first.toLowerCase(), ...rest].join(' ') : s
}

/**
 * A whole round of bonus trivia about one book, as a `DailyVerse` the shared
 * `QuizRunner` can run — what the library lends.
 *
 * It is ANCHORED ON A REAL VERSE from that book rather than being five bare
 * questions, for the reason every arcade machine hands its verse back: a round
 * of Bible facts with no scripture on the screen is a pub quiz. The verse is
 * read first, gets marked studied like any other study run, and is there to
 * keep at the end.
 *
 * `book` null draws across all 66.
 */
export function triviaVerseFromBook(book: string | null, seed: number): DailyVerse {
  const rng = mulberry32(seed >>> 0)
  const scoped = book ? VERSE_POOL.filter((v) => v.book === book) : VERSE_POOL
  const pool = scoped.length ? scoped : VERSE_POOL
  const pick = pool[Math.floor(rng() * pool.length)]
  const questions = triviaRoundFor(book, rng)
  // Fail closed the same way `generateQuestions` does: a build with no trivia
  // for this book gives an ordinary practice run rather than an empty screen.
  if (!questions.length) return buildDailyVerse(pick, rng, `trivia-${book ?? 'any'}`)
  return { ...buildDailyVerse(pick, rng, `trivia-${book ?? 'any'}`), questions }
}

// Assemble a full DailyVerse payload from a pool seed. Shared by the daily drop
// and by focus practice, so both produce identically-shaped runs.
function buildDailyVerse(seed: VerseSeed, rng: () => number, dropDate: string): DailyVerse {
  const questions = generateQuestions(seed, rng)
  return {
    dropDate,
    translation: DEFAULT_TRANSLATION,
    reference: seed.reference,
    book: seed.book,
    speaker: seed.speaker,
    testament: seed.testament,
    chapter: seed.chapter,
    verseStart: seed.verseStart,
    verseEnd: seed.verseEnd,
    text: seed.text,
    theme: seed.theme,
    questions,
    facts: seed.facts,
    contextBefore: seed.before,
    contextAfter: seed.after,
  }
}

// Pick the verse for a given date and build its full DailyVerse payload.
export function getVerseForDate(dateStr: string): DailyVerse {
  const rng = mulberry32(hashString(dateStr))
  // No-repeat rotation. One fixed shuffle of the whole pool, indexed by the day
  // number, so the sequence cycles through EVERY verse exactly once before any
  // repeat. Guarantees: a verse repeats only every VERSE_POOL.length days
  // (~8 months at 251 verses), never back-to-back, and every verse appears
  // equally often. The order is stable, which is what makes those guarantees hold.
  const [y, m, d] = dateStr.split('-').map(Number)
  const dayNum = Math.floor(Date.UTC(y || 1970, (m || 1) - 1, d || 1) / 86400000)
  const N = VERSE_POOL.length
  const order = shuffle(
    Array.from({ length: N }, (_, i) => i),
    mulberry32(hashString('verse-order-v1')),
  )
  const seed = VERSE_POOL[order[((dayNum % N) + N) % N]]
  return buildDailyVerse(seed, rng, dateStr)
}

// The day's trivia round — five questions about ONE book, and the same book and
// the same five questions for every player on a given date.
//
// It is the daily drop's bonus question turned into a whole round, which is why
// nothing here is invented: the book rotation is `getVerseForDate`'s no-repeat
// shuffle with its own seed, the questions come from `triviaRoundFor` (the same
// source the last slot of every run already draws from) and the round is
// ANCHORED ON A REAL VERSE from that book, exactly as the library's round is —
// a screenful of Bible facts with no scripture on it is a pub quiz.
//
// Two things about it are load-bearing:
//
//  - **The rotation is over books the VERSE POOL can anchor.** A book with
//    trivia but no pool verse would hand the anchor back to "any book" and the
//    round would ask about Obadiah over a verse from Luke. Fails closed the way
//    everything else here does: no eligible book at all ⇒ a whole-Bible round.
//  - **`'trivia-order-v1'` is a history seed like `'verse-order-v1'`.**
//    Changing it re-deals every past and future day, so don't.
export function dailyTriviaFor(dateStr: string): DailyVerse {
  const book = dailyTriviaBook(dateStr)
  const rng = mulberry32(hashString(`daily-trivia-${dateStr}`))
  const scoped = book ? VERSE_POOL.filter((v) => v.book === book) : VERSE_POOL
  const pool = scoped.length ? scoped : VERSE_POOL
  const pick = pool[Math.floor(rng() * pool.length)]
  // Drawn BEFORE the verse questions, so the round is stable for the date even
  // if `generateQuestions` ever changes how much rng it consumes.
  const questions = triviaRoundFor(book, rng)
  const base = buildDailyVerse(pick, rng, `daily-trivia-${dateStr}`)
  // Fail closed: a build with no trivia for this book gives an ordinary run
  // rather than an empty screen, the same as `triviaVerseFromBook`.
  return questions.length ? { ...base, questions } : base
}

/**
 * Which book a trivia battle is about, and the ONE place that is decided.
 *
 * It has its own rng rather than the first draw off the round's — otherwise the
 * label above the run and the round itself would be two copies of the same
 * derivation that have to agree, and this file already carries the scar of what
 * that costs (`dailyTriviaFor` and `dailyTriviaBook`, deduped for the same
 * reason). Nothing consumes this stream but the book.
 */
export function triviaBattleBook(seed: number): string | null {
  const books = dailyTriviaBooks()
  if (!books.length) return null
  const rng = mulberry32(hashString(`battle-trivia-book-${seed >>> 0}`))
  return books[Math.floor(rng() * books.length)]
}

/**
 * A trivia round from a battle seed — five questions about ONE book, over a
 * verse from that book, rebuilt identically on both players' devices.
 *
 * It is the machinery `dailyTriviaFor` uses, seeded off the battle's seed
 * instead of the date, and that is what a battle needs: `battleVerse` maps a
 * seed to a deal and both phones run it, so a trivia battle can only work if the
 * book is a function of the seed too. Nothing is sent.
 *
 * Fails closed like everything else here: no book in the pool has trivia ⇒ a
 * whole-Bible round rather than an empty one.
 */
export function triviaBattleVerse(seed: number): DailyVerse {
  const book = triviaBattleBook(seed)
  const rng = mulberry32(hashString(`battle-trivia-${seed >>> 0}`))
  const scoped = book ? VERSE_POOL.filter((v) => v.book === book) : VERSE_POOL
  const pool = scoped.length ? scoped : VERSE_POOL
  const pick = pool[Math.floor(rng() * pool.length)]
  const questions = triviaRoundFor(book, rng)
  const base = buildDailyVerse(pick, rng, `battle-trivia-${book ?? 'any'}`)
  return questions.length ? { ...base, questions } : base
}

/**
 * Which book the day's trivia round is about — the one place the rotation is
 * computed, so the card that offers the round and the round itself can't name
 * two different books. Null only if no book in the pool has trivia.
 */
export function dailyTriviaBook(dateStr: string): string | null {
  const books = dailyTriviaBooks()
  const N = books.length
  if (!N) return null
  const [y, m, d] = dateStr.split('-').map(Number)
  const dayNum = Math.floor(Date.UTC(y || 1970, (m || 1) - 1, d || 1) / 86400000)
  const order = shuffle(
    Array.from({ length: N }, (_, i) => i),
    mulberry32(hashString('trivia-order-v1')),
  )
  return books[order[((dayNum % N) + N) % N]]
}

/** Books that have trivia AND a verse in the pool to read the round on. */
function dailyTriviaBooks(): string[] {
  const inPool = new Set(VERSE_POOL.map((v) => v.book))
  return triviaBooks().filter((b) => inPool.has(b))
}

// Distinct books present in the verse pool, in canonical Bible order (Genesis →
// Revelation) rather than the order entries happen to sit in the pool file, so
// the picker reads like a table of contents no matter how the pool grows.
export function poolBooks(): string[] {
  const seen = new Set<string>()
  for (const v of VERSE_POOL) seen.add(v.book)
  const known = BIBLE_BOOKS.filter((b) => seen.has(b))
  // Anything not in the canonical list (a typo, or a naming variant) still shows
  // up rather than vanishing from the picker — just at the end, alphabetically.
  const extra = [...seen].filter((b) => !BIBLE_BOOKS.includes(b)).sort()
  return [...known, ...extra]
}

// How many verses the pool has per book — used to show a book's depth.
export function poolBookCounts(): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const v of VERSE_POOL) counts[v.book] = (counts[v.book] ?? 0) + 1
  return counts
}

// A random practice verse drawn from a single book (or any book when `book` is
// null), seeded so the same seed reproduces the same verse + questions.
export function practiceVerseFromBook(book: string | null, seed: number): DailyVerse {
  const rng = mulberry32(seed >>> 0)
  const scoped = book ? VERSE_POOL.filter((v) => v.book === book) : VERSE_POOL
  const pool = scoped.length ? scoped : VERSE_POOL
  const pick = pool[Math.floor(rng() * pool.length)]
  return buildDailyVerse(pick, rng, `focus-${book ?? 'any'}`)
}

// One verse by reference, as a full DailyVerse payload. Used by the favorites
// shelf, where a kept verse needs to open the chapter reader. Seeded off the
// reference so it's deterministic like everything else here, and independent of
// getVerseForDate — reading a favorite never disturbs the daily rotation.
export function verseFromReference(reference: string): DailyVerse | null {
  const seed = VERSE_POOL.find((v) => v.reference === reference)
  if (!seed) return null
  return buildDailyVerse(seed, mulberry32(hashString(`favorite-${reference}`)), `favorite-${reference}`)
}
